import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';

const PasswordPage = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [formErrors, setFormErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Limpiar errores
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }

    // Limpiar mensaje de éxito
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validateForm = () => {
    let isValid = true;
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    if (!formData.currentPassword.trim()) {
      errors.currentPassword = 'La contraseña actual es obligatoria';
      isValid = false;
    }

    if (!formData.newPassword.trim()) {
      errors.newPassword = 'La nueva contraseña es obligatoria';
      isValid = false;
    } else if (formData.newPassword.length < 6) {
      errors.newPassword = 'La contraseña debe tener al menos 6 caracteres';
      isValid = false;
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Debes confirmar la contraseña';
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulación de cambio de contraseña
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mostrar mensaje de éxito
      setSuccessMessage('Tu contraseña ha sido actualizada correctamente');

      // Limpiar el formulario
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Error al cambiar la contraseña:', error);
      setFormErrors({
        ...formErrors,
        currentPassword: 'La contraseña actual es incorrecta',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!auth.isAuthenticated) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f2ede4' }}>
      {/* Hero Section */}
      <div className="py-24 bg-white">
        <div className="content-container">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight" style={{ color: '#0d0d0d' }}>
              Cambiar Contraseña
            </h1>
            <p className="text-xl md:text-2xl font-light text-gray-600 mb-8 leading-relaxed">
              Actualiza tu contraseña para mantener tu cuenta segura
            </p>
            <Link
              to="/account"
              className="inline-flex items-center text-lg font-medium hover:underline transition-colors"
              style={{ color: '#0d0d0d' }}
            >
              ← Volver a mi cuenta
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-16">
        <div className="content-container">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-md overflow-hidden"
            >
              <div className="p-8">
                {successMessage && (
                  <motion.div
                    className="mb-6 rounded-2xl p-4 border-2"
                    style={{ backgroundColor: '#f0f9ff', borderColor: '#f6cf02' }}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex">
                      <div className="flex-shrink-0 p-1 rounded-full" style={{ backgroundColor: '#f6cf02' }}>
                        <svg className="h-5 w-5" style={{ color: '#0d0d0d' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="font-medium" style={{ color: '#0d0d0d' }}>{successMessage}</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                      Contraseña actual
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border-2 ${formErrors.currentPassword ? 'border-red-300' : 'border-gray-200'
                        } rounded-2xl shadow-sm py-3 px-4 focus:outline-none focus:border-gray-400 transition-colors`}
                      style={{ color: '#0d0d0d' }}
                    />
                    {formErrors.currentPassword && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.currentPassword}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                      Nueva contraseña
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border-2 ${formErrors.newPassword ? 'border-red-300' : 'border-gray-200'
                        } rounded-2xl shadow-sm py-3 px-4 focus:outline-none focus:border-gray-400 transition-colors`}
                      style={{ color: '#0d0d0d' }}
                    />
                    {formErrors.newPassword && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.newPassword}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirmar contraseña
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border-2 ${formErrors.confirmPassword ? 'border-red-300' : 'border-gray-200'
                        } rounded-2xl shadow-sm py-3 px-4 focus:outline-none focus:border-gray-400 transition-colors`}
                      style={{ color: '#0d0d0d' }}
                    />
                    {formErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-end space-x-4">
                    <Link
                      to="/account"
                      className="py-3 px-6 border-2 border-gray-300 rounded-full font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </Link>
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="py-3 px-6 rounded-full font-medium transition-all border-2 shadow-md hover:shadow-lg disabled:opacity-50"
                      style={{
                        backgroundColor: '#f6cf02',
                        color: '#0d0d0d',
                        borderColor: '#0d0d0d'
                      }}
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                      onMouseEnter={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.backgroundColor = '#0d0d0d';
                          e.currentTarget.style.color = '#f6cf02';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmitting) {
                          e.currentTarget.style.backgroundColor = '#f6cf02';
                          e.currentTarget.style.color = '#0d0d0d';
                        }
                      }}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 mr-3" style={{ borderColor: '#0d0d0d' }}></div>
                          Guardando...
                        </div>
                      ) : 'Cambiar contraseña'}
                    </motion.button>
                  </div>
                </form>
              </div>

              <div className="mt-8 p-6 rounded-2xl" style={{ backgroundColor: '#f2ede4' }}>
                <div className="text-sm text-gray-600">
                  <p className="font-bold mb-3" style={{ color: '#0d0d0d' }}>Recomendaciones de seguridad:</p>
                  <ul className="list-disc list-inside space-y-2 ml-2">
                    <li>Usa una contraseña de al menos 8 caracteres</li>
                    <li>Incluye letras mayúsculas, minúsculas, números y símbolos</li>
                    <li>No uses la misma contraseña que en otros sitios</li>
                    <li>Cambia tu contraseña regularmente</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordPage; 